import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { 
  Users, Plus, Trash2, UserCheck, Building2, 
  Mail, Copy, Eye, EyeOff, Shield, BarChart3, ShieldCheck, Lock, Pencil, Check, X, KeyRound, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { formatPeriod } from '@/lib/aiQuota';

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  admin_enterprise: 'bg-blue-100 text-blue-700 border-blue-200',
  client: 'bg-blue-100 text-blue-700 border-blue-200',
  employee: 'bg-green-100 text-green-700 border-green-200',
  marketing_seo: 'bg-orange-100 text-orange-700 border-orange-200',
};

const ROLE_DISPLAY = {
  admin: { fr: 'Admin', en: 'Admin' },
  admin_enterprise: { fr: 'Admin Entreprise', en: 'Enterprise Admin' },
  client: { fr: 'Admin Entreprise', en: 'Enterprise Admin' },
  employee: { fr: 'Employe', en: 'Employee' },
  marketing_seo: { fr: 'Marketing / SEO', en: 'Marketing / SEO' },
};

const PERM_LABELS = {
  can_view_dashboard: { fr: 'Voir tableau de bord', en: 'View dashboard' },
  can_view_leads: { fr: 'Voir prospects', en: 'View leads' },
  can_edit_leads: { fr: 'Modifier prospects', en: 'Edit leads' },
  can_manage_seo: { fr: 'Gerer SEO', en: 'Manage SEO' },
  can_manage_pages: { fr: 'Gerer pages', en: 'Manage pages' },
  can_view_analytics: { fr: 'Voir analytics', en: 'View analytics' },
  can_manage_forms: { fr: 'Gerer formulaires', en: 'Manage forms' },
  can_manage_users: { fr: 'Gerer utilisateurs', en: 'Manage users' },
  can_manage_settings: { fr: 'Gerer parametres', en: 'Manage settings' },
  can_manage_employees: { fr: 'Gerer employes', en: 'Manage employees' },
  can_manage_subcontractors: { fr: 'Gerer sous-traitants', en: 'Manage subcontractors' },
  can_manage_all_workspaces: { fr: 'Gerer toutes entreprises', en: 'Manage all workspaces' },
};

export function AdminPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tf = (fr, en) => language === 'fr' ? fr : en;
  
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [canManageRoles, setCanManageRoles] = useState(false);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showCreateRoleDialog, setShowCreateRoleDialog] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [newClient, setNewClient] = useState({
    name: '', email: '', company_name: '', temporary_password: ''
  });
  const [newRole, setNewRole] = useState({ name: '', label_fr: '', label_en: '', permissions: {} });
  const [isCreating, setIsCreating] = useState(false);

  // AI quotas (platform admin only)
  const isPlatformAdmin = user?.role === 'admin';
  const [aiQuotas, setAiQuotas] = useState([]);
  const [aiQuotasLoading, setAiQuotasLoading] = useState(false);
  const [editingQuotaWs, setEditingQuotaWs] = useState(null);
  const [extraInput, setExtraInput] = useState('');
  const [savingExtra, setSavingExtra] = useState(false);

  const loadAiQuotas = async () => {
    if (!isPlatformAdmin) return;
    setAiQuotasLoading(true);
    try {
      const { data } = await api.get('/admin/ai-quotas');
      setAiQuotas(data.workspaces || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : tf('Admin uniquement', 'Admin only');
      toast.error(msg);
    } finally {
      setAiQuotasLoading(false);
    }
  };

  const openExtraEditor = (ws) => {
    setEditingQuotaWs(ws);
    setExtraInput(String(ws.ai_generations_extra || 0));
  };

  const closeExtraEditor = () => {
    setEditingQuotaWs(null);
    setExtraInput('');
  };

  const saveExtra = async () => {
    if (!editingQuotaWs) return;
    const n = parseInt(extraInput, 10);
    if (Number.isNaN(n) || n < 0 || n > 500) {
      toast.error(tf('Quota extra invalide', 'Invalid extra quota'));
      return;
    }
    setSavingExtra(true);
    try {
      await api.patch(`/admin/workspaces/${editingQuotaWs.workspace_id}/ai-quota-extra`, {
        ai_generations_extra: n,
      });
      toast.success(tf('Quota extra mis à jour', 'Extra quota updated'));
      closeExtraEditor();
      await loadAiQuotas();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : tf('Erreur de mise à jour', 'Update error');
      toast.error(msg);
    } finally {
      setSavingExtra(false);
    }
  };

  useEffect(() => {
    const allowedRoles = ['admin', 'admin_enterprise'];
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    loadData();
    loadAiQuotas();
  }, [user]);

  const loadData = async () => {
    // Per-section resilience: a single endpoint failing must NOT blank
    // the entire AdminPage. Promise.allSettled lets us partial-render.
    const [clientsRes, statsRes, rolesRes] = await Promise.allSettled([
      api.get('/admin/clients'),
      api.get('/admin/stats'),
      api.get('/admin/roles'),
    ]);

    if (clientsRes.status === 'fulfilled') {
      setClients(clientsRes.value.data.clients);
    } else {
      console.error('Error loading clients:', clientsRes.reason);
      toast.error(tf('Erreur chargement utilisateurs', 'Failed to load users'));
    }

    if (statsRes.status === 'fulfilled') {
      setStats(statsRes.value.data);
    } else {
      console.error('Error loading stats:', statsRes.reason);
      toast.error(tf('Erreur chargement statistiques', 'Failed to load stats'));
    }

    if (rolesRes.status === 'fulfilled') {
      setRoles(rolesRes.value.data.roles);
      setAllPermissions(rolesRes.value.data.all_permissions || []);
      setCanManageRoles(rolesRes.value.data.can_manage_roles || false);
    } else {
      console.error('Error loading roles:', rolesRes.reason);
      toast.error(tf('Erreur chargement rôles', 'Failed to load roles'));
    }

    setIsLoading(false);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) password += chars.charAt(Math.floor(Math.random() * chars.length));
    setNewClient({ ...newClient, temporary_password: password });
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.name || !newClient.email || !newClient.company_name || !newClient.temporary_password) {
      toast.error(tf('Tous les champs sont requis', 'All fields are required'));
      return;
    }
    setIsCreating(true);
    try {
      await api.post('/admin/clients', newClient);
      setCreatedCredentials({ email: newClient.email, password: newClient.temporary_password, company_name: newClient.company_name, name: newClient.name });
      setNewClient({ name: '', email: '', company_name: '', temporary_password: '' });
      setShowCreateDialog(false);
      setShowPasswordDialog(true);
      loadData();
      toast.success(tf('Client cree!', 'Client created!'));
    } catch (err) {
      toast.error(err.response?.data?.detail || tf('Erreur', 'Error'));
    } finally { setIsCreating(false); }
  };

  const toggleClientStatus = async (clientId, currentStatus) => {
    try {
      await api.put(`/admin/clients/${clientId}?is_active=${!currentStatus}`);
      setClients(prev => prev.map(c => c.user_id === clientId ? { ...c, is_active: !currentStatus } : c));
      toast.success(tf('Statut mis a jour', 'Status updated'));
    } catch (err) { toast.error(tf('Erreur', 'Error')); }
  };

  const deleteClient = async (clientId, email) => {
    if (!window.confirm(tf(`Supprimer le client ${email}?`, `Delete client ${email}?`))) return;
    try {
      await api.delete(`/admin/clients/${clientId}`);
      setClients(prev => prev.filter(c => c.user_id !== clientId));
      toast.success(tf('Client supprime', 'Client deleted'));
    } catch (err) { toast.error(tf('Erreur', 'Error')); }
  };

  /**
   * Send a password reset link to the given client. The toast message is
   * intentionally GENERIC (matches the backend's anti-enumeration response)
   * so even if the user is Google-only or inactive, the admin sees the
   * same outcome and cannot leak that information through their UI.
   */
  const sendResetLink = async (clientId, email) => {
    if (!window.confirm(tf(
      `Envoyer un lien de réinitialisation de mot de passe à ${email} ?`,
      `Send a password reset link to ${email}?`
    ))) return;
    try {
      await api.post(`/admin/clients/${clientId}/send-reset-link`);
      toast.success(tf(
        'Lien envoyé si le compte est éligible.',
        'Reset link sent if the account is eligible.'
      ));
    } catch (err) {
      const code = err.response?.status;
      if (code === 403) {
        toast.error(tf('Action réservée à l\'admin plateforme.', 'Platform admin only.'));
      } else if (code === 429) {
        toast.error(tf('Trop de demandes. Réessayez plus tard.', 'Too many requests. Try again later.'));
      } else {
        toast.error(tf('Erreur lors de l\'envoi.', 'Failed to send.'));
      }
    }
  };

  const assignRole = async (userId, newRoleVal) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRoleVal });
      setClients(prev => prev.map(c => c.user_id === userId ? { ...c, role: newRoleVal } : c));
      toast.success(tf('Role mis a jour', 'Role updated'));
    } catch (err) { toast.error(err.response?.data?.detail || tf('Erreur', 'Error')); }
  };

  const setClientPlan = async (userId, newPlan) => {
    try {
      await api.put(`/admin/clients/${userId}/plan`, { plan: newPlan });
      setClients(prev => prev.map(c =>
        c.user_id === userId
          ? { ...c, workspace: { ...(c.workspace || {}), plan: newPlan } }
          : c
      ));
      toast.success(tf('Forfait mis a jour', 'Plan updated'));
    } catch (err) { toast.error(err.response?.data?.detail || tf('Erreur', 'Error')); }
  };

  const startEditRole = (role) => {
    setEditingRole(role.role);
    setEditPerms({ ...role.permissions });
  };

  const cancelEditRole = () => {
    setEditingRole(null);
    setEditPerms({});
  };

  const saveEditRole = async (roleName) => {
    try {
      await api.put(`/admin/roles/${roleName}`, { permissions: editPerms });
      setEditingRole(null);
      loadData();
      toast.success(tf('Permissions mises a jour', 'Permissions updated'));
    } catch (err) { toast.error(err.response?.data?.detail || tf('Erreur', 'Error')); }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRole.name || !newRole.label_fr || !newRole.label_en) {
      toast.error(tf('Nom et labels requis', 'Name and labels required'));
      return;
    }
    try {
      await api.post('/admin/roles', {
        role: newRole.name,
        label: { fr: newRole.label_fr, en: newRole.label_en },
        permissions: newRole.permissions,
      });
      setShowCreateRoleDialog(false);
      setNewRole({ name: '', label_fr: '', label_en: '', permissions: {} });
      loadData();
      toast.success(tf('Role cree!', 'Role created!'));
    } catch (err) { toast.error(err.response?.data?.detail || tf('Erreur', 'Error')); }
  };

  const handleDeleteRole = async (roleName) => {
    if (!window.confirm(tf(`Supprimer le role "${roleName}"?`, `Delete role "${roleName}"?`))) return;
    try {
      await api.delete(`/admin/roles/${roleName}`);
      loadData();
      toast.success(tf('Role supprime', 'Role deleted'));
    } catch (err) { toast.error(err.response?.data?.detail || tf('Erreur', 'Error')); }
  };

  const copyCredentials = () => {
    const text = `${tf('Vos acces SpaceLeadX', 'Your SpaceLeadX Access')}\n---\n${tf('Courriel', 'Email')}: ${createdCredentials.email}\n${tf('Mot de passe temporaire', 'Temporary Password')}: ${createdCredentials.password}\n\n${tf('Connectez-vous sur', 'Login at')}: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text);
    toast.success(tf('Copie!', 'Copied!'));
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  const assignableRoles = roles.filter(r => r.is_assignable);

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg"><Shield className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">{tf('Panel Admin', 'Admin Panel')}</h1>
            <p className="text-muted-foreground">{tf('Gestion des comptes et des roles', 'Account and role management')}</p>
          </div>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-client-btn"><Plus className="mr-2 h-4 w-4" />{tf('Nouvelle entreprise', 'New Company')}</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{tf('Creer un compte entreprise', 'Create Company Account')}</DialogTitle>
              <DialogDescription>{tf('Le role Admin Entreprise sera attribue automatiquement.', 'Enterprise Admin role will be assigned automatically.')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div className="space-y-2"><Label>{tf('Nom', 'Name')}</Label><Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="Jean Dupont" required data-testid="new-client-name" /></div>
              <div className="space-y-2"><Label>{tf('Courriel', 'Email')}</Label><Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="client@exemple.com" required data-testid="new-client-email" /></div>
              <div className="space-y-2"><Label>{tf("Nom de l'entreprise", 'Company Name')}</Label><Input value={newClient.company_name} onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })} placeholder="Plomberie ABC" required data-testid="new-client-company" /></div>
              <div className="space-y-2">
                <Label>{tf('Mot de passe temporaire', 'Temporary Password')}</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input type={showPassword ? 'text' : 'password'} value={newClient.temporary_password} onChange={(e) => setNewClient({ ...newClient, temporary_password: e.target.value })} required data-testid="new-client-password" />
                    <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button type="button" variant="outline" onClick={generatePassword}>{tf('Generer', 'Generate')}</Button>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>{tf('Annuler', 'Cancel')}</Button>
                <Button type="submit" disabled={isCreating} data-testid="submit-create-client">{isCreating ? tf('Creation...', 'Creating...') : tf('Creer le compte', 'Create Account')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { icon: Users, color: 'text-primary', val: stats.total_clients, label: tf('Utilisateurs', 'Users') },
            { icon: UserCheck, color: 'text-green-500', val: stats.active_clients, label: tf('Actifs', 'Active') },
            { icon: Building2, color: 'text-blue-500', val: stats.total_workspaces, label: tf('Espaces', 'Workspaces') },
            { icon: BarChart3, color: 'text-orange-500', val: stats.total_leads, label: tf('Prospects', 'Leads') },
            { icon: Mail, color: 'text-purple-500', val: stats.total_forms, label: tf('Formulaires', 'Forms') },
          ].map(({ icon: Icon, color, val, label }) => (
            <Card key={label}><CardContent className="pt-6"><div className="flex items-center gap-3"><Icon className={`h-8 w-8 ${color} opacity-70`} /><div><p className="text-2xl font-bold">{val}</p><p className="text-sm text-muted-foreground">{label}</p></div></div></CardContent></Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-2" />{tf('Utilisateurs', 'Users')}</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles"><ShieldCheck className="h-4 w-4 mr-2" />{tf('Gestion des roles', 'Role Management')}</TabsTrigger>
          {isPlatformAdmin && (
            <TabsTrigger value="ai-quotas" data-testid="tab-ai-quotas">
              <Sparkles className="h-4 w-4 mr-2" />{tf('Quotas IA', 'AI Quotas')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>{tf('Comptes utilisateurs', 'User Accounts')}</CardTitle>
              <CardDescription>{tf('Gerez les acces, roles et comptes', 'Manage access, roles and accounts')}</CardDescription>
            </CardHeader>
            <CardContent>
              {clients.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">{tf('Aucun utilisateur', 'No users')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-semibold text-sm">{tf('Utilisateur', 'User')}</th>
                        <th className="text-left p-4 font-semibold text-sm">{tf('Entreprise', 'Company')}</th>
                        <th className="text-left p-4 font-semibold text-sm">{tf('Forfait', 'Plan')}</th>
                        <th className="text-left p-4 font-semibold text-sm">{tf('Role', 'Role')}</th>
                        <th className="text-left p-4 font-semibold text-sm">{tf('Statut', 'Status')}</th>
                        <th className="text-left p-4 font-semibold text-sm">{tf('Actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {clients.map((client) => {
                        const roleKey = client.role || 'client';
                        return (
                          <tr key={client.user_id} className="hover:bg-muted/30" data-testid={`user-row-${client.user_id}`}>
                            <td className="p-4"><p className="font-semibold">{client.name}</p><p className="text-sm text-muted-foreground">{client.email}</p></td>
                            <td className="p-4"><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span>{client.company_name || client.workspace?.name || '-'}</span></div></td>
                            <td className="p-4">
                              {client.workspace ? (
                                <Select
                                  value={client.workspace?.plan || 'free'}
                                  onValueChange={(val) => setClientPlan(client.user_id, val)}
                                >
                                  <SelectTrigger className="w-[140px] h-8" data-testid={`plan-select-${client.user_id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="free">{tf('Starter (Gratuit)', 'Starter (Free)')}</SelectItem>
                                    <SelectItem value="pro">Pro</SelectItem>
                                    <SelectItem value="enterprise">Enterprise</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-4">
                              <Select value={roleKey === 'client' ? 'admin_enterprise' : roleKey} onValueChange={(val) => assignRole(client.user_id, val)}>
                                <SelectTrigger className="w-[180px] h-8" data-testid={`role-select-${client.user_id}`}>
                                  <SelectValue>
                                    <Badge variant="outline" className={ROLE_COLORS[roleKey] || ROLE_COLORS.client}>
                                      {(ROLE_DISPLAY[roleKey] || ROLE_DISPLAY.client)[language]}
                                    </Badge>
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {assignableRoles.map(r => (
                                    <SelectItem key={r.role} value={r.role}>
                                      <Badge variant="outline" className={ROLE_COLORS[r.role] || 'bg-gray-100 text-gray-700'}>
                                        {r.label[language]}
                                      </Badge>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Switch checked={client.is_active !== false} onCheckedChange={() => toggleClientStatus(client.user_id, client.is_active !== false)} data-testid={`toggle-status-${client.user_id}`} />
                                <Badge variant={client.is_active !== false ? 'default' : 'secondary'}>{client.is_active !== false ? tf('Actif', 'Active') : tf('Inactif', 'Inactive')}</Badge>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-1">
                                {user?.role === 'admin' && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-primary"
                                    onClick={() => sendResetLink(client.user_id, client.email)}
                                    data-testid={`send-reset-${client.user_id}`}
                                    title={tf('Envoyer un lien de réinitialisation', 'Send reset link')}
                                  >
                                    <KeyRound className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteClient(client.user_id, client.email)} data-testid={`delete-user-${client.user_id}`}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles">
          <div className="space-y-4">
            {/* Admin: Create Role button */}
            {canManageRoles && (
              <div className="flex justify-end">
                <Dialog open={showCreateRoleDialog} onOpenChange={setShowCreateRoleDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-role-btn"><Plus className="mr-2 h-4 w-4" />{tf('Creer un role', 'Create Role')}</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{tf('Creer un nouveau role', 'Create a New Role')}</DialogTitle>
                      <DialogDescription>{tf('Definissez les permissions pour ce role.', 'Define the permissions for this role.')}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateRole} className="space-y-4">
                      <div className="space-y-2"><Label>{tf('Identifiant (sans espace)', 'Identifier (no spaces)')}</Label><Input value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} placeholder="gestionnaire" required data-testid="new-role-name" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2"><Label>{tf('Label FR', 'Label FR')}</Label><Input value={newRole.label_fr} onChange={(e) => setNewRole({ ...newRole, label_fr: e.target.value })} placeholder="Gestionnaire" required data-testid="new-role-label-fr" /></div>
                        <div className="space-y-2"><Label>{tf('Label EN', 'Label EN')}</Label><Input value={newRole.label_en} onChange={(e) => setNewRole({ ...newRole, label_en: e.target.value })} placeholder="Manager" required data-testid="new-role-label-en" /></div>
                      </div>
                      <div className="space-y-2">
                        <Label>{tf('Permissions', 'Permissions')}</Label>
                        <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg max-h-48 overflow-y-auto">
                          {allPermissions.map(perm => (
                            <label key={perm} className="flex items-center gap-2 cursor-pointer text-sm">
                              <input type="checkbox" checked={newRole.permissions[perm] || false} onChange={(e) => setNewRole({ ...newRole, permissions: { ...newRole.permissions, [perm]: e.target.checked } })} className="rounded" />
                              {(PERM_LABELS[perm] || { fr: perm, en: perm })[language]}
                            </label>
                          ))}
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShowCreateRoleDialog(false)}>{tf('Annuler', 'Cancel')}</Button>
                        <Button type="submit" data-testid="submit-create-role">{tf('Creer', 'Create')}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {/* Info banner for non-super-admin */}
            {!canManageRoles && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <Lock className="h-5 w-5 text-blue-500 shrink-0" />
                    <p className="text-sm text-blue-700">
                      {tf(
                        'Vous pouvez assigner des roles aux utilisateurs mais seul l\'Admin peut creer, modifier ou supprimer des roles.',
                        'You can assign roles to users but only the Admin can create, modify or delete roles.'
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Role cards */}
            <div className="grid gap-4 md:grid-cols-2">
              {roles.map(role => {
                const isEditing = editingRole === role.role;
                const permsToShow = isEditing ? editPerms : role.permissions;
                return (
                  <Card key={role.role} data-testid={`role-card-${role.role}`} className={isEditing ? 'ring-2 ring-primary' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-base">
                          {role.role === 'admin' ? <Lock className="h-4 w-4 text-red-500" /> : <ShieldCheck className="h-4 w-4" />}
                          <Badge variant="outline" className={ROLE_COLORS[role.role] || 'bg-gray-100 text-gray-700'}>{role.label[language]}</Badge>
                          {role.is_system && <span className="text-xs text-muted-foreground">{tf('Systeme', 'System')}</span>}
                        </CardTitle>
                        <div className="flex items-center gap-1">
                          {!role.is_assignable && <Badge variant="secondary" className="text-xs">{tf('Unique', 'Unique')}</Badge>}
                          {canManageRoles && role.role !== 'admin' && !isEditing && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditRole(role)} data-testid={`edit-role-${role.role}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canManageRoles && !role.is_system && !isEditing && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteRole(role.role)} data-testid={`delete-role-${role.role}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isEditing && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => saveEditRole(role.role)} data-testid={`save-role-${role.role}`}><Check className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={cancelEditRole} data-testid={`cancel-role-${role.role}`}><X className="h-4 w-4" /></Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(permsToShow).map(([perm, enabled]) => (
                          <div key={perm} className="flex items-center gap-2">
                            {isEditing ? (
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => setEditPerms({ ...editPerms, [perm]: e.target.checked })}
                                className="rounded"
                              />
                            ) : (
                              <div className={`w-2 h-2 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                            )}
                            <span className={`text-xs ${enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                              {(PERM_LABELS[perm] || { fr: perm, en: perm })[language]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* AI Quotas Tab — platform admin only */}
        {isPlatformAdmin && (
        <TabsContent value="ai-quotas">
          <Card data-testid="ai-quotas-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    {tf('Quotas IA', 'AI Quotas')}
                  </CardTitle>
                  <CardDescription>
                    {tf(
                      'Suivi mensuel des générations IA par workspace. Vous pouvez accorder des générations supplémentaires manuellement.',
                      'Monthly tracking of AI generations per workspace. You can grant extra generations manually.'
                    )}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadAiQuotas} disabled={aiQuotasLoading} data-testid="refresh-ai-quotas">
                  {aiQuotasLoading ? tf('Chargement...', 'Loading...') : tf('Actualiser', 'Refresh')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ai-quotas-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-2">{tf('Entreprise / Workspace', 'Workspace')}</th>
                      <th className="p-2">{tf('Plan', 'Plan')}</th>
                      <th className="p-2 text-center">{tf('Utilisé', 'Used')}</th>
                      <th className="p-2 text-center">{tf('Base', 'Base')}</th>
                      <th className="p-2 text-center">{tf('Extra', 'Extra')}</th>
                      <th className="p-2 text-center">{tf('Total', 'Total')}</th>
                      <th className="p-2 text-center">{tf('Restant', 'Remaining')}</th>
                      <th className="p-2">{tf('Période', 'Period')}</th>
                      <th className="p-2 text-right">{tf('Action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {aiQuotas.map((w) => {
                      const totalDisplay = w.ai_generations_limit_total === -1 ? '∞' : w.ai_generations_limit_total;
                      const remainingDisplay = w.ai_generations_remaining === -1 ? '∞' : w.ai_generations_remaining;
                      const exhausted = w.ai_generations_limit_total !== -1 && w.ai_generations_count >= w.ai_generations_limit_total;
                      return (
                        <tr key={w.workspace_id} className="hover:bg-muted/20" data-testid={`ai-quota-row-${w.workspace_id}`}>
                          <td className="p-2">
                            <div className="font-medium">{w.name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{w.slug}</div>
                          </td>
                          <td className="p-2">
                            <Badge variant="outline" className="capitalize">{w.plan}</Badge>
                          </td>
                          <td className="p-2 text-center font-mono tabular-nums">
                            <span className={exhausted ? 'text-destructive font-bold' : ''}>
                              {w.ai_generations_count} / {totalDisplay}
                            </span>
                          </td>
                          <td className="p-2 text-center tabular-nums">{w.ai_generations_limit_base === -1 ? '∞' : w.ai_generations_limit_base}</td>
                          <td className="p-2 text-center tabular-nums">{w.ai_generations_extra}</td>
                          <td className="p-2 text-center tabular-nums font-medium">{totalDisplay}</td>
                          <td className="p-2 text-center tabular-nums">{remainingDisplay}</td>
                          <td className="p-2 text-xs text-muted-foreground">
                            {formatPeriod(
                              w.ai_generations_period || new Date().toISOString().slice(0, 7),
                              language
                            )}
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openExtraEditor(w)}
                              data-testid={`add-extra-${w.workspace_id}`}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" />
                              {tf('Ajouter extra', 'Add extra')}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {aiQuotas.length === 0 && !aiQuotasLoading && (
                      <tr><td colSpan={9} className="text-center text-muted-foreground py-8">{tf('Aucun workspace', 'No workspaces')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Extra quota editor */}
      <Dialog open={!!editingQuotaWs} onOpenChange={(o) => { if (!o) closeExtraEditor(); }}>
        <DialogContent className="sm:max-w-md" data-testid="extra-quota-dialog">
          <DialogHeader>
            <DialogTitle>{tf('Ajouter quota extra', 'Add extra quota')}</DialogTitle>
            <DialogDescription>
              {editingQuotaWs && (
                <span>
                  {editingQuotaWs.name} — <span className="capitalize">{editingQuotaWs.plan}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="extra-input">
              {tf('Générations supplémentaires (0–500)', 'Extra generations (0–500)')}
            </Label>
            <Input
              id="extra-input"
              type="number"
              min={0}
              max={500}
              value={extraInput}
              onChange={(e) => setExtraInput(e.target.value)}
              data-testid="extra-quota-input"
              autoFocus
            />
            {editingQuotaWs && (
              <p className="text-xs text-muted-foreground">
                {tf('Base du plan :', 'Plan base:')} {editingQuotaWs.ai_generations_limit_base === -1 ? '∞' : editingQuotaWs.ai_generations_limit_base}
                {' · '}
                {tf('Total après mise à jour :', 'New total:')}
                {' '}
                <span className="font-mono font-medium">
                  {editingQuotaWs.ai_generations_limit_base === -1
                    ? '∞'
                    : editingQuotaWs.ai_generations_limit_base + (parseInt(extraInput, 10) || 0)}
                </span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeExtraEditor} disabled={savingExtra}>
              {tf('Annuler', 'Cancel')}
            </Button>
            <Button onClick={saveExtra} disabled={savingExtra} data-testid="save-extra-quota">
              {savingExtra ? tf('Enregistrement...', 'Saving...') : tf('Enregistrer', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600"><UserCheck className="h-5 w-5" />{tf('Compte cree!', 'Account Created!')}</DialogTitle>
            <DialogDescription>{tf('Envoyez ces informations au client.', 'Send this information to the client.')}</DialogDescription>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div><p className="text-sm text-muted-foreground">{tf('Client', 'Client')}</p><p className="font-medium">{createdCredentials.name}</p></div>
                <div><p className="text-sm text-muted-foreground">{tf('Entreprise', 'Company')}</p><p className="font-medium">{createdCredentials.company_name}</p></div>
                <Separator />
                <div><p className="text-sm text-muted-foreground">{tf('Courriel', 'Email')}</p><p className="font-mono font-medium">{createdCredentials.email}</p></div>
                <div><p className="text-sm text-muted-foreground">{tf('Mot de passe temporaire', 'Temporary Password')}</p><p className="font-mono font-medium text-primary">{createdCredentials.password}</p></div>
              </div>
              <Button onClick={copyCredentials} className="w-full"><Copy className="mr-2 h-4 w-4" />{tf('Copier les acces', 'Copy Credentials')}</Button>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowPasswordDialog(false)}>{tf('Fermer', 'Close')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
